import React from 'react';

const SecurityComplianceHub = () => {
  const sections = [
    {
      title: 'Architecture & Data Protection',
      id: 'architecture-and-data-protection',
      items: [
        'VPC isolation',
        'private subnets',
        'mutual TLS',
        'encryption in transit (TLS 1.2+)',
        'encryption at rest (KMS/HSM)',
        'customer‑managed keys',
        'secrets via Vault/KMS',
        'network egress controls',
        'data minimization & retention policies',
      ],
    },
    {
      title: 'Identity & Access',
      id: 'identity-and-access',
      items: [
        'SSO/SAML/OIDC',
        'MFA',
        'least‑privilege RBAC/ABAC',
        'just‑in‑time access',
        'break‑glass procedures',
        'PAM for admin actions',
      ],
    },
    {
      title: 'Secure SDLC',
      id: 'secure-sdlc',
      items: [
        'Threat modeling',
        'SAST/DAST',
        'IaC scanning',
        'dependency/SBOM & supply‑chain (SLSA/Sigstore)',
        'container image scanning',
        'pre‑prod pen‑tests',
      ],
    },
    {
      title: 'ML‑Specific Controls',
      id: 'ml-specific-controls',
      items: [
        'Dataset lineage & consent',
        'PII/PHI de‑identification',
        'DLP',
        'prompt‑injection & jailbreak defenses',
        'output filtering',
        'evals/scorecards for safety',
        'retrieval isolation (namespace/tenancy)',
        'encrypted vector stores',
      ],
    },
    {
      title: 'Monitoring & IR',
      id: 'monitoring-and-ir',
      items: [
        'Centralized logging/SIEM',
        'anomaly detection',
        'audit trails',
        'incident response runbooks',
        'RTO/RPO targets',
        'immutable backups',
        'disaster recovery testing',
      ],
    },
    {
      title: 'HIPAA Experience',
      id: 'hipaa-experience',
      items: [
        'PHI segmentation',
        'BAA readiness',
        'minimum necessary access',
        'auditability',
        'breach notification workflows',
      ],
    },
    {
      title: 'GDPR Experience',
      id: 'gdpr-experience',
      items: [
        'Lawful basis assessment',
        'DPIAs',
        'DSR handling (access/erase/export)',
        'data localization',
        'processor/sub‑processor reviews',
      ],
    },
    {
      title: 'SOC 2 Experience',
      id: 'soc2-experience',
      items: [
        'Control mapping across Security/Availability/Confidentiality',
        'change‑management',
        'vendor risk',
        'ticketing evidence',
      ],
    },
  ];

  const note = 'We operate under HIPAA, SOC 2, and GDPR‑aligned practices and have extensive implementation experience.';
  const lead = 'We implement controls aligned to HIPAA, SOC 2 (Type 2 principles), and GDPR. Typical program:';

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16 pb-24">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 -z-10 opacity-50">
            <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />
            <div className="absolute top-1/3 -right-24 h-96 w-96 rounded-full bg-indigo-500/20 blur-3xl" />
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              <span className="text-sm text-foreground/90">{note}</span>
            </div>
            <p className="mt-6 text-xl text-foreground/90">{lead}</p>
          </div>
        </section>

        {/* Sticky quick-nav */}
        <nav className="sticky top-16 z-10 border-y border-white/10 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <ul className="flex gap-3 overflow-x-auto py-3">
              {sections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-foreground/90 hover:bg-white/10 transition"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Content grid */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 mt-10">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {sections.map((section) => (
              <div
                key={section.id}
                id={section.id}
                className="group relative rounded-2xl border border-white/10 bg-white/5 p-6 shadow-[0_0_0_1px_rgba(255,255,255,0.04)_inset] hover:shadow-lg hover:shadow-primary/10 transition"
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition" />
                <div className="relative">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center">
                      <svg viewBox="0 0 24 24" className="h-5 w-5 text-primary" aria-hidden="true">
                        <path fill="currentColor" d="M12 2l3 7h7l-5.5 4 2.5 7-7-4.5L5 20l2.5-7L2 9h7z" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-semibold">{section.title}</h2>
                  </div>
                  <ul className="list-disc pl-6 space-y-2 text-foreground/90">
                    {section.items.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom note */}
          <div className="mt-12 rounded-2xl border border-white/10 bg-white/5 p-6">
            <p className="text-base font-medium text-foreground/90">“{note}”</p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default SecurityComplianceHub;